// src/components/EventList.js
import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import "./EventList.css";
import AdminEventTabs from "./AdminEventTabs";

const formatDate = (isoDate) => {
    if (!isoDate) return "MM/DD";
    const datePart = isoDate.split("T")[0];
    return datePart.substring(5).replace("-", "/");
};

const formatTime = (iso) => {
    if (!iso) return "--:--";
    const t = iso.includes("T") ? iso.split("T")[1] : iso;
    return t.substring(0, 5);
};

const STATUS_LABEL = { published: "公開中", draft: "一時保存", cancelled: "中止済み" };

/** 一覧・詳細どちらのキーでもイベント名を取得 */
const resolveEventName = (item) => {
    if (!item || typeof item !== "object") return "";
    const name = item.eventName ?? item.event_name ?? item.title ?? "";
    return String(name).trim();
};

const PAGE_SIZE = 10;

const EventList = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [filterStatus, setFilterStatus] = useState("published");
    const [sortOrder, setSortOrder] = useState("asc");
    const [currentPage, setCurrentPage] = useState(1);
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();

    const fetchCurrentUser = async () => {
        try {
            const res = await api.get("/auth/me/");
            setCurrentUser(res.data);
        } catch (err) {
            console.error("ユーザー情報取得エラー:", err);
        }
    };

    const fetchEvents = async () => {
            try {
                const res = await api.get(`/events/?_t=${Date.now()}`);
                const eventsWithStats = await Promise.all(
                    res.data.map(async (event) => {
                        let countParticipating = 0;
                        let countAbsent = 0;
                        let countUnregistered = 0;
                        try {
                            const statsRes = await api.get(`/events/${event.id}/results/?_t=${Date.now()}`);
                            const participants = statsRes.data;
                            countParticipating = participants.filter(p => p.is_participating === true).length;
                            countAbsent = participants.filter(p => p.is_participating === false).length;
                            countUnregistered = participants.filter(p => p.is_participating === null || p.is_participating === undefined).length;
                        } catch {}
                        return { ...event, stats: { participating: countParticipating, absent: countAbsent, unregistered: countUnregistered } };
                    })
                );
                setEvents(eventsWithStats);
            } catch (err) {
                setError("イベント情報の取得に失敗しました。");
            } finally {
                setLoading(false);
            }
    };

    useEffect(() => {
        fetchCurrentUser();
        fetchEvents();
    }, []);

    // フィルタ & ソート（公開中・中止済みは開催日が今日以降のみ。一時保存は日付に関係なく全件）
    const filtered = useMemo(() => {
        const d = new Date();
        const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        let list = [...events];
        if (filterStatus === "draft") {
            list = list.filter(e => e.status === "draft" && !e.is_cancelled);
        } else {
            list = list.filter(e => {
                const datePart = (e.eventDate || "").toString().split("T")[0];
                if (!datePart) return false;
                return datePart >= todayStr;
            });
            if (filterStatus === "published") {
                list = list.filter(e => e.status === "published" && !e.is_cancelled);
            } else if (filterStatus === "cancelled") {
                list = list.filter(e => e.is_cancelled);
            }
        }
        if (searchText.trim()) {
            const q = searchText.trim().toLowerCase();
            list = list.filter(e => (e.eventName || "").toLowerCase().includes(q));
        }
        list.sort((a, b) => {
            const da = new Date(a.eventDate || 0);
            const db = new Date(b.eventDate || 0);
            return sortOrder === "asc" ? da - db : db - da;
        });
        return list;
    }, [events, searchText, filterStatus, sortOrder]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const handleSearch = (e) => { setSearchText(e.target.value); setCurrentPage(1); };
    const handleFilterStatus = (e) => { setFilterStatus(e.target.value); setCurrentPage(1); };

    /** 「2/14（金）」形式。曜日まで入れて、社員がどのイベントか判別しやすくする */
    const formatDateWithWeekday = (isoDate) => {
        if (!isoDate) return "";
        const datePart = String(isoDate).split("T")[0];
        const d = new Date(`${datePart}T00:00:00`);
        if (isNaN(d.getTime())) return datePart;
        const w = ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
        return `${d.getMonth() + 1}/${d.getDate()}（${w}）`;
    };

    /**
     * イベント中止 → お知らせ作成画面へ。
     * 中止の確定（is_cancelled）はお知らせを作成できた時点で行う。
     * ここで先に中止すると、お知らせを作らずに離脱したときに
     * 社員へ何も届かないままイベントだけ消えてしまうため。
     */
    const goCancelNotice = async (eventItem, clickEv) => {
        clickEv.stopPropagation();
        const name = resolveEventName(eventItem) || "イベント";
        const dateLabel = formatDateWithWeekday(eventItem.eventDate);

        // 宛先はイベントの対象者を自動選択する。取得できなくても遷移は行い、
        // その場合は管理者が画面上で選ぶ。
        // お知らせAPI の recipients は memberId（社員番号）の文字列を受け取る
        // （NotificationCreateSerializer が SlugRelatedField(slug_field="memberId")）。
        // DB の id を渡すと 400 になるので注意。
        let recipientMemberIds = [];
        try {
            const res = await api.get(`/events/${eventItem.id}/target_employees/?_t=${Date.now()}`);
            recipientMemberIds = (Array.isArray(res.data) ? res.data : [])
                .map((e) => e.memberId)
                .filter(Boolean);
        } catch {
            /* 宛先の自動選択のみ失敗。お知らせ作成自体は続行できる */
        }

        const body =
            `${dateLabel ? `${dateLabel}開催予定の` : ""}「${name}」は中止となりました。\n\n` +
            `理由：（ここに入力）\n\n` +
            `ご参加を予定されていた皆様には、申し訳ございません。`;

        navigate("/admin/notice", {
            state: {
                eventCancel: {
                    eventId: eventItem.id,
                    eventName: name,
                    title: `【中止】${name}`,
                    content: body,
                    recipientMemberIds,
                },
            },
        });
    };


    // 削除は論理削除（is_deleted=True）。アンケート回答や配信済みのお知らせは残る。
    const handleDelete = async (eventItem, clickEv) => {
        clickEv.stopPropagation();
        const nameLabel = resolveEventName(eventItem) || "このイベント";
        if (!window.confirm(
            `「${nameLabel}」を削除しますか？\n` +
            "一覧から表示されなくなります。アンケートの回答や配信済みのお知らせは残ります。"
        )) {
            return;
        }
        setDeletingId(eventItem.id);
        try {
            await api.delete(`/events/${eventItem.id}/`);
            setEvents((prev) => prev.filter((ev) => ev.id !== eventItem.id));
        } catch (err) {
            alert(err.response?.data?.error || "削除に失敗しました。");
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) return <div className="event-list-container"><p className="loading-text">読み込み中...</p></div>;
    if (error) return <div className="event-list-container"><p className="error-text">{error}</p></div>;

    return (
        <div className="event-list-container">
            <AdminEventTabs active="list" currentUser={currentUser} />
            {/* ===== ヘッダー ===== */}
            
            <div className="el-header">
                {/*<button className="el-btn-back" onClick={() => navigate("/admin/create_event")}> 前画面に戻る</button> */}
                <h2 className="el-title">イベント一覧</h2>
                {/*<Link to="/event/create" className="el-btn-create">＋ 新規作成</Link>*/}
            </div>
            
            {/* ===== 検索・フィルタバー ===== */}
            <div className="el-toolbar">
                <input
                    type="text"
                    className="el-search"
                    placeholder="イベント名で検索..."
                    value={searchText}
                    onChange={handleSearch}
                />
                <select className="el-select" value={filterStatus} onChange={handleFilterStatus}>
                    {/*<option value="all">すべて</option> */}
                    <option value="published">公開中</option>
                    <option value="draft">一時保存</option>
                    <option value="cancelled">中止済み</option>
                </select>
                <select className="el-select" value={sortOrder} onChange={e => { setSortOrder(e.target.value); setCurrentPage(1); }}>
                    <option value="asc">開催日 昇順</option>
                    <option value="desc">開催日 降順</option>
                </select>
                <span className="el-count">{filtered.length} 件</span>
            </div>

            {/* ===== イベントカード一覧 ===== */}
            {paginated.length === 0 ? (
                filterStatus !== "draft" ? (
                    <p className="no-events-message">該当するイベントはありません。</p>
                ) : null
            ) : (
                <div className="el-list">
                    {paginated.map((eventItem) => {
                        const displayName = resolveEventName(eventItem) || "未設定";
                        return (
                        /*
                        <div
                            key={eventItem.id}
                            className={`el-card${eventItem.is_cancelled ? " el-card-cancelled" : ""}`}
                            onClick={() => navigate(`/event/reduce/${eventItem.id}`)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => e.key === "Enter" && navigate(`/event/reduce/${eventItem.id}`)}
                        >
                        */
                        <div
                            key={eventItem.id}
                            className={`el-card${eventItem.is_cancelled ? " el-card-cancelled" : ""}`}
                            style={{ cursor: "default" }}
                        >
                            <div className="el-card-layout">
                                {/* 左列：イベント名 / 日時・場所 or 中止枠 / 参加統計 */}
                                <div className="el-card-col-left">
                                    <div className="el-card-row el-card-row-title">
                                        <span className="el-event-name">{displayName}</span>
                                        {eventItem.status === "draft" && (
                                            <span className="el-badge el-badge-draft">{STATUS_LABEL.draft}</span>
                                        )}
                                        {eventItem.is_cancelled && (
                                            <span className="el-badge el-badge-cancelled">{STATUS_LABEL.cancelled}</span>
                                        )}
                                    </div>

                                    <div className="el-card-row el-card-row-middle">
                                        {eventItem.is_cancelled ? (
                                            <div className="el-cancelled-panel">
                                                <p className="el-cancelled-panel-status">このイベントは中止済みです</p>
                                                {eventItem.cancel_reason ? (
                                                    <p className="el-cancelled-panel-reason">
                                                        <span className="el-cancelled-panel-label">中止理由</span>
                                                        {eventItem.cancel_reason}
                                                    </p>
                                                ) : (
                                                    <p className="el-cancelled-panel-reason el-cancelled-panel-reason-empty">中止理由は登録されていません</p>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="el-card-meta">
                                                <span className="el-meta-item">
                                                    <span className="el-meta-label">開催日</span>
                                                    {formatDate(eventItem.eventDate)}
                                                    {eventItem.eventEndDate && eventItem.eventEndDate !== eventItem.eventDate
                                                        ? ` 〜 ${formatDate(eventItem.eventEndDate)}`
                                                        : ""}
                                                </span>
                                                <span className="el-meta-item">
                                                    <span className="el-meta-label">時間</span>
                                                    {formatTime(eventItem.startTime)} 〜 {formatTime(eventItem.endTime)}
                                                </span>
                                                {eventItem.regDeadline && (
                                                    <span className="el-meta-item">
                                                        <span className="el-meta-label">締切</span>
                                                        {formatDate(eventItem.regDeadline)}
                                                    </span>
                                                )}
                                                {eventItem.location && (
                                                    <span className="el-meta-item">
                                                        <span className="el-meta-label">場所</span>
                                                        {eventItem.location.length > 30 ? eventItem.location.substring(0, 30) + "…" : eventItem.location}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="el-card-row el-card-row-stats">
                                        <div className="el-stats">
                                            <span className="el-stat el-stat-yes">参加 {eventItem.stats.participating}名</span>
                                            <span className="el-stat el-stat-no">欠席 {eventItem.stats.absent}名</span>
                                            <span className="el-stat el-stat-pending">未登録 {eventItem.stats.unregistered}名</span>
                                        </div>
                                    </div>
                                </div>

                                {/* 右列：上=アンケートあり / 中=参加者管理 / 下=イベント中止 */}
                                <div
                                    className="el-card-col-actions"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                >
                                    <div className="el-action-slot el-action-slot-top">
                                        {eventItem.hasAnketo ? (
                                            <Link
                                                to={`/event/${eventItem.id}/get_responses`}
                                                className="el-btn-action el-btn-anketo"
                                            >
                                                アンケートあり
                                            </Link>
                                        ) : null}
                                    </div>
                                    <div className="el-action-slot el-action-slot-mid">
                                        <Link
                                            to={`/event/department/confirm/${eventItem.id}`}
                                            className="el-btn-action el-btn-participants"
                                        >
                                            参加者管理
                                        </Link>
                                    </div>
                                    {/* 修正 */}
                                    {!eventItem.is_cancelled && (
                                        <div className="el-action-slot">
                                            <Link
                                                to={`/event/reduce/${eventItem.id}`}
                                                className="el-btn-action el-btn-edit"
                                            >
                                                修正
                                            </Link>
                                        </div>
                                    )}
                                    <div className="el-action-slot el-action-slot-bot">
                                        {/* 公開中は「イベント中止」、一時保存・中止済みは「削除」。
                                            公開中を予告なく消さないよう、削除は中止後か一時保存のみに限定する。 */}
                                        {eventItem.status === "published" && !eventItem.is_cancelled ? (
                                            <button
                                                type="button"
                                                className="el-btn-action el-btn-cancel"
                                                onClick={(clickEv) => goCancelNotice(eventItem, clickEv)}
                                            >
                                                イベント中止
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="el-btn-action el-btn-delete"
                                                onClick={(clickEv) => handleDelete(eventItem, clickEv)}
                                                disabled={deletingId === eventItem.id}
                                            >
                                                {deletingId === eventItem.id ? "削除中…" : "削除"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {/* ===== ページネーション ===== */}
            {totalPages > 1 && (
                <div className="el-pagination">
                    <button
                        className="el-page-btn"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                    >
                        ‹ 前へ
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <button
                            key={p}
                            className={`el-page-btn ${p === currentPage ? "active" : ""}`}
                            onClick={() => setCurrentPage(p)}
                        >
                            {p}
                        </button>
                    ))}
                    <button
                        className="el-page-btn"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                    >
                        次へ ›
                    </button>
                </div>
            )}
        </div>
    );
};

export default EventList;
